
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import { ChatState, Faq, ChatMessage } from "./types";

const CHAT_API_URL =
  process.env.REACT_APP_CHAT_SERVICE_URL ||
  "http://localhost:9887/api/v1/chat";

const api = axios.create({
  baseURL: CHAT_API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token") || getCookie("access_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

export const fetchFaqs = createAsyncThunk(
  "chat/fetchFaqs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/customer/faqs");
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch FAQs");
    }
  }
);

export const fetchChatHistory = createAsyncThunk(
  "chat/fetchChatHistory",
  async (chatId: string | undefined, { rejectWithValue }) => {
    try {
      const url = chatId ? `/customer/chats/${chatId}/messages` : "/customer/chats/history";
      const response = await api.get(url);
      return {
        messages: response.data.data || [],
        chatId: chatId || (response.data.data.length > 0 ? response.data.data[0].chatId : null)
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch chat history");
    }
  }
);

export const searchFaqs = createAsyncThunk(
  "chat/searchFaqs",
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/customer/faqs/search?q=${query}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Search failed");
    }
  }
);

const initialState: ChatState = {
  chatId: null,
  messages: [],
  faqs: [],
  suggestedQuestions: [],
  isBotActive: true,
  isAgentJoined: false,
  agentName: null,
  loading: false,
  error: null,
  faqLoading: false,
  chatHistoryLoading: false,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setChatId: (state, action: PayloadAction<string>) => {
      state.chatId = action.payload;
    },
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    setMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = action.payload;
    },
    setSuggestedQuestions: (state, action: PayloadAction<string[]>) => {
      state.suggestedQuestions = action.payload;
    },
    setAgentJoined: (state, action: PayloadAction<{ agentName: string }>) => {
      state.isAgentJoined = true;
      state.agentName = action.payload.agentName;
      state.isBotActive = false;
    },
    resetChat: (state) => {
      state.chatId = null;
      state.messages = [];
      state.isBotActive = true;
      state.isAgentJoined = false;
      state.agentName = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFaqs.pending, (state) => {
        state.faqLoading = true;
      })
      .addCase(fetchFaqs.fulfilled, (state, action: PayloadAction<Faq[]>) => {
        state.faqLoading = false;
        state.faqs = action.payload;
      })
      .addCase(fetchFaqs.rejected, (state, action) => {
        state.faqLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchChatHistory.pending, (state) => {
        state.chatHistoryLoading = true;
      })
      .addCase(fetchChatHistory.fulfilled, (state, action) => {
        state.chatHistoryLoading = false;
        state.messages = action.payload.messages;
        state.chatId = action.payload.chatId;
      })
      .addCase(fetchChatHistory.rejected, (state, action) => {
        state.chatHistoryLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  setChatId, 
  addMessage, 
  setMessages, 
  setSuggestedQuestions, 
  setAgentJoined, 
  resetChat 
} = chatSlice.actions;

export default chatSlice.reducer;
